import React from "react";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Grow from "@material-ui/core/Grow";
import Paper from "@material-ui/core/Paper";
import Popper from "@material-ui/core/Popper";
import MenuItem from "@material-ui/core/MenuItem";
import MenuList from "@material-ui/core/MenuList";
import { makeStyles } from "@material-ui/core/styles";
import { navigate } from "@reach/router";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { scroller } from "react-scroll";
import styled from "styled-components";
import useCartCount from "../../../hooks/useCartCount";

const useStyles = makeStyles((theme) => ({
  menuButton: {
    display: "flex",
    alignItems: "center",
    padding: "10px 5px",
    fontFamily: "openSans-SemiBold, sans-serif",
    fontSize: "16px",
    fontWeight: "500",
    color: "#293461",
    background: "transparent",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "#f0f4f8",
      color: "#00818a",
    },
    "&:focus": {
      outline: "none",
    },
  },
  arrow: {
    marginLeft: "8px",
    fontSize: "20px",
    transition: "transform 0.3s ease",
  },
  arrowOpen: {
    transform: "rotate(180deg)",
  },
  paper: {
    marginTop: "8px",
    marginRight: theme.spacing(2),
    borderRadius: "12px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.12)",
    overflow: "hidden",
    minWidth: "200px",
  },
  menuItem: {
    fontFamily: "openSans-Regular, sans-serif",
    fontSize: "16px",
    padding: "12px 20px",
    color: "#293461",
    transition: "background-color 0.2s ease",
    display: "flex",
    justifyContent: "space-between",
    "&:hover": {
      backgroundColor: "#e6f3f4",
      color: "#00818a",
    },
    "&:focus": {
      backgroundColor: "#e6f3f4",
      outline: "none",
    },
  },
}));

const MenuButtonWrapper = styled.div`
  display: inline-flex;
`;

export default function DropDownMenu(props) {
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const [submenuAnchor, setSubmenuAnchor] = React.useState(null);
  const [submenuItems, setSubmenuItems] = React.useState([]);
  const anchorRef = React.useRef(null);
  const { setScrollTick } = useCartCount();

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
    setSubmenuAnchor(null);
  };

  function handleListKeyDown(event) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === "Escape") {
      setOpen(false);
      anchorRef.current.focus();
    }
  }

  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  return (
    <MenuButtonWrapper>
      <button
        ref={anchorRef}
        aria-controls={open ? "menu-list-grow" : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        className={classes.menuButton}
      >
        {props.title}
        <ExpandMoreIcon
          className={`${classes.arrow} ${open ? classes.arrowOpen : ""}`}
        />
      </button>

      {/* Main Menu */}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement="bottom-start"
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom-start" ? "left top" : "left bottom",
            }}
          >
            <Paper className={classes.paper} elevation={8}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="menu-list-grow"
                  onKeyDown={handleListKeyDown}
                >
                  {props.options.map((option) =>
                    option.children ? (
                      <MenuItem
                        key={option.name}
                        className={classes.menuItem}
                        onMouseEnter={(e) => {
                          setSubmenuAnchor(e.currentTarget);
                          setSubmenuItems(option.children);
                        }}
                      >
                        {option.name}
                        <ChevronRightIcon />
                      </MenuItem>
                    ) : (
                      <MenuItem
                        key={option.name}
                        onMouseEnter={() => {
                          setSubmenuAnchor(null);
                          setSubmenuItems([]);
                        }}
                        onClick={(event) => {
                          handleClose(event);
                          if (
                            option.link ===
                            process.env.REACT_APP_NEUTRAL_BASE_URL
                          ) {
                            window.open(
                              option.link,
                              "_blank",
                              "noopener,noreferrer"
                            );
                          } else {
                            navigate(option.link);
                            setScrollTick((prev) => prev + 1);
                          }
                          if (option.section) {
                            scroller.scrollTo(option.section, {
                              duration: 600,
                              offset: -70,
                              smooth: "easeInOutQuart",
                            });
                          }
                        }}
                        className={classes.menuItem}
                      >
                        {option.name}
                      </MenuItem>
                    )
                  )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      {/* Submenu */}
      <Popper
        open={Boolean(submenuAnchor)}
        anchorEl={submenuAnchor}
        role={undefined}
        transition
        disablePortal
        placement="right-start"
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: "left top" }}>
            <Paper className={classes.paper} elevation={8}>
              <ClickAwayListener onClickAway={() => setSubmenuAnchor(null)}>
                <MenuList>
                  {submenuItems.map((sub) => (
                    <MenuItem
                      key={sub.name}
                      onClick={() => {
                        window.open(sub.link);
                        setScrollTick((prev) => prev + 1);
                        setSubmenuAnchor(null);
                        setOpen(false);
                      }}
                      className={classes.menuItem}
                    >
                      {sub.name}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </MenuButtonWrapper>
  );
}
